// CreditsContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../../backend/useUser';
import { getCurrentToken } from '../../utils/auth';
import { SubscriptionType, CreditInfo, CREDITS_CONSTANTS, useCredits, OutOfCredits } from '../../backend/useCredits';
import { useLocation } from 'react-router-dom';

export const plans = [
  { key: 'free', name: 'FREE' },
  { key: 'trial', name: 'TRIAL' },
  { key: 'hobby', name: 'HOBBY' },
  { key: 'creator', name: 'CREATOR' }
];

interface CreditsContextType {
  isOutOfCredits: OutOfCredits;
  credits: CreditInfo;
  subscription: SubscriptionType;
  isLoading: boolean;
  error: Error | null;
  refetchCreditsData: () => Promise<void>;
  isUpsellModalOpen: boolean;
  setIsUpsellModalOpen: (isOpen: boolean) => void;
}

const defaultContext: CreditsContextType = {
  isOutOfCredits: {
    is_limited_overall: false,
    game_create_limited: false,
    game_edit_limited: false,
    art_2d_limited: false,
    bg_remove_limited: false,
    art_3d_limited: false,
    animate_limited: false
  },
  credits: { creditsUsed: 0, creditsRemaining: 0, totalCredits: 0, nextResetDate: null },
  subscription: 'free',
  isLoading: false,
  error: null,
  refetchCreditsData: async () => {},
  isUpsellModalOpen: false,
  setIsUpsellModalOpen: (isOpen: boolean) => {},
};

const CreditsContext = createContext<CreditsContextType>(defaultContext);

export const useCreditsContext = () => useContext(CreditsContext);

export const CreditsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user } = useUser();
  const { getIsOutOfCredits, getCredits, getSubscription } = useCredits();
  const [isOutOfCredits, setIsOutOfCredits] = useState<OutOfCredits>(defaultContext.isOutOfCredits);
  const [credits, setCredits] = useState<CreditInfo>({ creditsUsed: 0, creditsRemaining: 0, totalCredits: 0, nextResetDate: null });
  // Initialize subscription from localStorage if available, otherwise use default
  const [subscription, setSubscription] = useState<SubscriptionType>(
    (localStorage.getItem('subscription') as SubscriptionType) || 'free'
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState<boolean>(false);

  // useEffect(() => {
  //   console.log('CREDITS_CONTEXT_RENDER: Current user received from useUser:', user?.id ? 'User ID present' : 'No User ID', 'Loading state:', isLoading);
  // }, [user, isLoading]);

  // useEffect(() => {
  //   console.log('CREDITS CONTEXT user:', user);
  // }, [user]);

  // useEffect(() => {
  //   console.log('CREDITS CONTEXT subscription:', subscription);
  // }, [subscription]);

  // useEffect(() => {
  //   console.log('credits context localStorage subscription:', localStorage.getItem('subscription'));
  // }, [localStorage.getItem('subscription')]);

  // useEffect(() => {
  //   console.log('credits context localStorage user:', localStorage.getItem('userId'), localStorage.getItem('userEmail'));
  // }, [localStorage.getItem('userId')]);

  // useEffect(() => {
  //   console.log('is out of credits', isOutOfCredits);
  // }, [isOutOfCredits]);

  const checkAndGrantCredits = useCallback(async (userId: string) => {
    // console.log('CREDITS_CONTEXT: Calling manage-credits function for userId:', userId);
    // console.log('Calling manage-credits function with params:', {
    //   userId: userId,
    //   action: 'checkGrantCredits'
    // });
    
    try {
      const accessToken = await getCurrentToken();
      
      if (!accessToken) {
        console.error('CREDITS_CONTEXT: No access token available for manage-credits call for userId:', userId);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/manage-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId: userId,
          action: 'checkGrantCredits'
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error calling manage-credits function:', {
          status: response.status,
          statusText: response.statusText,
          errorDetails: errorText
        });
      } else {
        // console.log('Successfully called manage-credits function, status:', response.status);
        try {
          const responseData = await response.json();
          // console.log('Response data from manage-credits:', responseData);
        } catch (jsonError) {
          console.warn('Response was not JSON format, text:', await response.text());
        }
      }
    } catch (error) {
      console.error('CREDITS_CONTEXT: Error calling manage-credits function:', error);
    } finally {
      // console.log('CREDITS_CONTEXT: Completed credit check process for user:', userId);
      // console.log('Completed credit check process for user:', userId);
    }
  }, []);

  const fetchCreditsData = useCallback(async () => {
    // console.log('CREDITS_CONTEXT: fetchCreditsData initiated. Current user.id:', user?.id, 'Current user.session:', user?.session ? 'present' : 'null');
    if (!user?.id && !user?.session) { // Added user?.session check
      // console.log('CREDITS_CONTEXT: fetchCreditsData aborted: user ID or session is missing.');
      setIsLoading(false); // Ensure loading is set to false if we abort
      return;
    }
    setError(null);
    setIsLoading(true); // Set loading to true here

    try {
      // Check and grant credits first
      await checkAndGrantCredits(user.id);
      
      // Fetch all data in parallel for efficiency
      const [outOfCreditsResult, creditsResult, subscriptionResult] = await Promise.all([
        getIsOutOfCredits(user.id),
        getCredits(user.id),
        getSubscription(user.id)
      ]);

      setIsOutOfCredits(outOfCreditsResult);
      setCredits(creditsResult);
      setSubscription(subscriptionResult);
      if (subscriptionResult !== 'free') {
        localStorage.setItem('subscription', subscriptionResult);
        // console.log('CREDITS_CONTEXT: Stored subscription in localStorage:', subscriptionResult);
      } else {
          // console.log('CREDITS_CONTEXT: Subscription is free, not storing in localStorage.'); // Added log
          localStorage.removeItem('subscription'); // Ensure it's removed if free
      }
      // console.log('CREDITS_CONTEXT: Successfully fetched all credit data for user:', user.id);
    } catch (err) {
      console.error('CREDITS_CONTEXT: Error fetching usage data:', err);
      setError(err instanceof Error ? err : new Error('Unknown error fetching usage data'));
    } finally {
      setIsLoading(false);
      // console.log('CREDITS_CONTEXT: fetchCreditsData completed.');
    }
  }, [user, getIsOutOfCredits, getCredits, getSubscription, checkAndGrantCredits]);

  // Fetch data when user or url changes
  useEffect(() => {
    // console.log('CREDITS_CONTEXT_EFFECT: Triggered by user change. User:', user?.id ? 'present' : 'null');
    if (user?.id) {
      // console.log('CREDITS_CONTEXT_EFFECT: User ID present, calling fetchCreditsData.');
      fetchCreditsData();
    } else {
      // console.log('CREDITS_CONTEXT_EFFECT: No user ID, resetting credit states.');
      // Reset to defaults if no user
      setIsOutOfCredits(defaultContext.isOutOfCredits);
      setCredits(defaultContext.credits);
      setSubscription(defaultContext.subscription);
      localStorage.removeItem('subscription');
    }
  }, [user, fetchCreditsData, location.pathname]);

  const refetchCreditsData = useCallback(async () => {
    await fetchCreditsData();
  }, [fetchCreditsData]);

  // Handle tab focus events to re-check isOutOfCredits
  const lastFocusTimeRef = useRef<number>(Date.now());
  
  useEffect(() => {
    // Function to handle when the window regains focus or visibility state changes
    const handleVisibilityChange = () => {
      const now = Date.now();
      const timeSinceLastFocus = now - lastFocusTimeRef.current;
      
      // Only refetch if the tab is visible and it's been more than 5 seconds since last focus
      // This is specifically helpful for refreshing state after a payment in another tab
      if (document.visibilityState === 'visible' && user?.id) {
        if (timeSinceLastFocus > 5000) {
          // console.log('Tab visible or regained focus, re-checking usage data');
          refetchCreditsData();
        }
        lastFocusTimeRef.current = now;
      }
    };
    
    // Add event listeners for both focus and visibilitychange
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    
    // Clean up event listeners on unmount
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [refetchCreditsData, user]);

  const value = {
    isOutOfCredits,
    credits,
    subscription,
    isLoading,
    error,
    refetchCreditsData,
    isUpsellModalOpen,
    setIsUpsellModalOpen,
  };

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
};